import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { PageWithTitle } from 'src/Common/components';
import {
  useCosts,
  useCostsMonthly,
  useCreateCost,
  useDeleteCost,
  useRealPnl,
  PredictionCost,
} from '../api/useCosts';

const formatUsd = (value: number | undefined | null) => {
  if (value == null || isNaN(value)) return '-';
  return (
    '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  );
};

const pnlColor = (pnl: number | undefined | null) => {
  if (pnl == null) return 'inherit';
  if (pnl > 0) return '#4caf50';
  if (pnl < 0) return '#f44336';
  return 'inherit';
};

const SummaryCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Paper
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 180,
      backgroundColor: 'background.paper',
    }}
  >
    <Typography
      sx={{
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        color: 'grey.600',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color }}>
      {value}
    </Typography>
  </Paper>
);

export const CostsPage = () => {
  const { data: realPnl } = useRealPnl();
  const { data: monthly } = useCostsMonthly();
  const { data: costs } = useCosts();
  const { mutate: createCost, isLoading: creating } = useCreateCost();
  const { mutate: deleteCost } = useDeleteCost();

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [confirmDelete, setConfirmDelete] = useState<PredictionCost | null>(
    null
  );

  const canSubmit = useMemo(() => {
    const num = parseFloat(amount);
    return description.trim().length > 0 && !isNaN(num) && num > 0;
  }, [description, amount]);

  const handleAdd = () => {
    if (!canSubmit) return;
    createCost(
      {
        description: description.trim(),
        amountUsd: parseFloat(amount),
        date: new Date(date).toISOString(),
      },
      {
        onSuccess: () => {
          setDescription('');
          setAmount('');
        },
      }
    );
  };

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        <Stack direction="row" alignItems="center" mb={1.5}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Costs &amp; Real P&amp;L
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2}>
          <SummaryCard
            label="P&L"
            value={formatUsd(realPnl?.pnlUsd)}
            color={pnlColor(realPnl?.pnlUsd)}
          />
          <SummaryCard
            label="Total Costs"
            value={formatUsd(realPnl?.totalCostsUsd)}
            color="#f44336"
          />
          <SummaryCard
            label="Real P&L"
            value={formatUsd(realPnl?.realPnlUsd)}
            color={pnlColor(realPnl?.realPnlUsd)}
          />
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              color: 'grey.600',
              mb: 1,
            }}
          >
            Add Cost
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              sx={{ flex: 2, minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Amount (USD)"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!canSubmit || creating}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 320 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              Month-by-Month
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Costs</TableCell>
                  <TableCell align="right"># Entries</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthly?.monthly.map(row => (
                  <TableRow key={row.month}>
                    <TableCell>{row.month}</TableCell>
                    <TableCell align="right">
                      {formatUsd(row.totalUsd)}
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
                {monthly && monthly.monthly.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 1.5, flex: 2, minWidth: 420 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              All Costs
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {costs?.map(cost => (
                  <TableRow key={cost._id}>
                    <TableCell>
                      {new Date(cost.date).toISOString().split('T')[0]}
                    </TableCell>
                    <TableCell>{cost.description}</TableCell>
                    <TableCell align="right">
                      {formatUsd(cost.amountUsd)}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete(cost)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {costs && costs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Stack>

        <Dialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete cost?</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.82rem' }}>
              {confirmDelete?.description} — {formatUsd(confirmDelete?.amountUsd)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDelete) {
                  deleteCost(confirmDelete._id);
                  setConfirmDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithTitle>
  );
};
